/**
 * Panier FRS.
 * <frs-cart-page>   : quantités et code promo, rendu par la Section Rendering API.
 * <frs-cart-drawer> : idem, dans un <dialog> modal (focus piégé, Échap natif).
 * Chaque mise à jour émet CartLinesUpdateEvent pour la pastille du header.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';

async function postJSON(url, body) {
  const response = await fetch(`${root()}${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Erreur');
  return data;
}

class FrsCart extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
  }

  get content() {
    return this.querySelector('[data-cart-content]');
  }

  render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-content]');
    if (fresh) this.content.innerHTML = fresh.innerHTML;
    const count = doc.querySelector('[data-cart-count]');
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (count) el.textContent = count.textContent;
    });
  }

  async refresh() {
    const response = await fetch(`${root()}?sections=${this.sectionId}`);
    const data = await response.json();
    this.render(data[this.sectionId]);
  }

  /** Lance une mise à jour du panier, rafraîchit la section et prévient le reste du thème. */
  async update(url, body, lines = []) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise }));
    this.content.classList.add('is-busy');
    try {
      const cart = await postJSON(url, { ...body, sections: this.sectionId });
      this.render(cart.sections?.[this.sectionId]);
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { itemCount: cart.item_count } });
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    } finally {
      this.content.classList.remove('is-busy');
    }
  }

  onClick = async (event) => {
    const qty = event.target.closest('[data-qty]');
    if (qty) {
      await this.update('cart/change.js', { id: qty.dataset.line, quantity: Number(qty.dataset.qty) }).catch(() => this.refresh());
      return;
    }

    const toggle = event.target.closest('[data-promo-toggle]');
    if (toggle) {
      const form = this.querySelector('[data-promo-form]');
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      form.hidden = !open;
      if (open) form.querySelector('input')?.focus();
      return;
    }

    const remove = event.target.closest('[data-discount-remove]');
    if (remove) {
      const codes = this.codes().filter((c) => c !== remove.dataset.discountRemove);
      await this.update('cart/update.js', { discount: codes.join(',') }).catch(() => this.refresh());
    }
  };

  onSubmit = async (event) => {
    const form = event.target.closest('[data-promo-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('input');
    const code = input.value.trim().toUpperCase();
    if (!code) return;
    const errorText = this.dataset.promoError || 'Ce code n’est pas valable.';
    try {
      const cart = await this.update('cart/update.js', { discount: [...this.codes(), code].join(',') });
      const applied = (cart.discount_codes || []).find((d) => d.code.toUpperCase() === code);
      if (!applied || !applied.applicable) {
        const codes = (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
        await this.update('cart/update.js', { discount: codes.join(',') });
        this.showPromoError(errorText, code);
      }
    } catch {
      this.showPromoError(errorText, code);
    }
  };

  codes() {
    const holder = this.querySelector('[data-codes]');
    return (holder?.dataset.codes || '').split(',').filter(Boolean);
  }

  showPromoError(text, code) {
    const toggle = this.querySelector('[data-promo-toggle]');
    const form = this.querySelector('[data-promo-form]');
    if (!toggle || !form) return;
    toggle.setAttribute('aria-expanded', 'true');
    form.hidden = false;
    form.querySelector('input').value = code;
    form.querySelector('[data-promo-error]').textContent = text;
  }
}

class FrsCartDrawer extends FrsCart {
  connectedCallback() {
    super.connectedCallback();
    this.dialog = this.querySelector('[data-dialog]');
    this.querySelector('[data-cart-close]').addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      document.querySelectorAll('[data-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    });
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-open]');
      if (!opener) return;
      event.preventDefault();
      this.open();
    });
    // Ajouts au panier venant d'autres composants (applis, Horizon) : on rafraîchit.
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.target === this || this.contains(event.target)) return;
      event.promise?.then(() => this.refresh()).catch(() => {});
    });
  }

  async open(sections) {
    if (sections?.[this.sectionId]) this.render(sections[this.sectionId]);
    else this.refresh();
    if (!this.dialog.open) {
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      document.querySelectorAll('[data-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    }
  }

  close() {
    this.dialog.close();
  }
}

if (!customElements.get('frs-cart-page')) customElements.define('frs-cart-page', FrsCart);
if (!customElements.get('frs-cart-drawer')) customElements.define('frs-cart-drawer', FrsCartDrawer);
